/**
 * The call view (docs/ux/artboards/call/ios-call.html at 390, web.html's docked panel on desktop):
 * - header: Minimise, the voice mode (Local · Live voice, which is not built yet: "Soon"), timer;
 * - blocked: one clear state with one primary action (unlock, or why the Mac can't take it);
 * - live: queue progress "3 of 7", the item card (verbatim options + always-visible note) or
 *   "Nothing needs you — ask anything", live captions, the state line with the calm waveform,
 *   the passkey batch, and mute / hold to talk / end;
 * - ended: a summary (duration, answered, approved, still waiting with links), Call again / Done.
 * Leaving the view never ends the call (useCallExits): it shrinks to the "On call" pill.
 */
import { Link } from '@tanstack/react-router'
import {
  AudioLines,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CircleSlash,
  Diamond,
  KeyRound,
  Laptop,
  LoaderCircle,
  Lock,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  ShieldCheck,
  SkipForward,
  X,
  Zap,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useCall } from '../../lib/call/CallProvider.js'
import { LIVE_VOICE_ENABLED } from '../../lib/call/live-voice.js'
import { blockerCopy, callBlockerFrom, liveProblemText } from '../../lib/call/problems.js'
import {
  type CallState,
  callSummary,
  currentItem,
  effectiveQueue,
  queueProgress,
} from '../../lib/call/state.js'
import { homeHref } from '../../lib/ia.js'
import { machineFailureText } from '../../lib/unlock-ceremony.js'
import { useContentLock } from '../lock/ContentLock.js'
import { CallItemCard } from './CallItemCard.js'
import { CallTimer, formatCallTime } from './CallTimer.js'
import { CallWave } from './CallWave.js'
import { isPhoneLayout, leaveCallView, useCallExits } from './useCallExits.js'
import './call.css'

/** The Home card's mode label ("Local · on-device speech"). */
export function modeLabel(path: string | undefined, label: string | undefined) {
  if (path === 'browser-local') return 'Local · on-device speech'
  if (path === 'mac-whisper') return `Local · ${label ?? 'whisper on your Mac'}`
  return 'Local · typing (no local speech here)'
}

/** Under the mode switch: how this call hears you, in plain words. */
export function privacyLine(s: Pick<CallState, 'speech' | 'machine'>, mac?: string): string {
  const m = s.machine?.name ?? mac ?? 'your Mac'
  if (s.speech?.path === 'browser-local') return `Private · speech stays on this device`
  if (s.speech?.path === 'mac-whisper') return `Private · speech transcribed on ${m}`
  if (s.speech?.path === 'none') return `Private · type to talk (no on-device speech here)`
  return `Private · end-to-end encrypted to ${m}`
}

export function CallDock() {
  const c = useCall()
  const s = c.state
  useCallExits(c)
  if (s.phase === 'idle' || s.view === 'min') return null
  const ended = s.phase === 'ended'
  return (
    <aside
      className={`call-dock is-${s.phase}`}
      aria-label="Call"
      data-testid="call-dock"
      data-phase={s.phase}
      tabIndex={-1}
    >
      <header className="call-hd">
        <button
          type="button"
          className="call-min"
          aria-label={ended || s.phase === 'blocked' ? 'Close call' : 'Minimise call'}
          title={
            ended || s.phase === 'blocked'
              ? 'Close (Esc)'
              : 'Minimise: the call keeps running (Esc)'
          }
          onClick={() => leaveCallView(c)}
        >
          <ChevronDown size={22} aria-hidden="true" className="call-min__phone" />
          <X size={18} aria-hidden="true" className="call-min__desk" />
        </button>
        {ended || s.phase === 'blocked' || s.phase === 'error' ? <span /> : <ModeSwitch />}
        {s.startedAt && !ended && s.phase !== 'blocked' ? <CallTimer since={s.startedAt} /> : null}
      </header>
      {s.phase === 'blocked' || s.phase === 'error' ? (
        <BlockedView />
      ) : ended ? (
        <EndedView />
      ) : (
        <LiveView />
      )}
    </aside>
  )
}

/** Local · Live voice. Live voice isn't built yet: disabled, with a small "Soon". */
function ModeSwitch() {
  const c = useCall()
  const s = c.state
  const live = false // VC7: becomes the call's mode once Live voice ships
  return (
    <div className="call-hd__mode">
      <div className="call-modesw" role="radiogroup" aria-label="Voice for this call">
        <button
          type="button"
          role="radio"
          aria-checked={!live}
          className="call-modesw__opt is-on"
          title="Local: speech is recognised on this device or your Mac; only encrypted text reaches your Mac. No per-minute cost."
        >
          <ShieldCheck size={16} aria-hidden="true" className="call-ok" />
          Local
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={live}
          disabled={!LIVE_VOICE_ENABLED}
          className="call-modesw__opt"
          title="Live voice (coming soon): quicker, more natural turns through a realtime speech provider, outside Viko's encryption, with a daily cost cap."
        >
          <Zap size={16} aria-hidden="true" />
          Live voice
          {!LIVE_VOICE_ENABLED ? <span className="call-soon">Soon</span> : null}
        </button>
      </div>
      {s.phase !== 'blocked' && s.phase !== 'error' ? (
        <p className="call-private" data-testid="call-private">
          {privacyLine(s, c.macStatus?.name)}
        </p>
      ) : null}
      {/* The cost meter appears only while Live voice is on (never in Local). */}
    </div>
  )
}

function MacStatus() {
  const c = useCall()
  const m = c.macStatus
  if (!m) return <p className="call-mac is-off">No Mac is set up for calls yet</p>
  return (
    <p className={`call-mac${m.online ? ' is-on' : ' is-off'}`} data-testid="call-mac-status">
      <Laptop size={16} aria-hidden="true" />
      <span>
        {m.name} · {m.online ? 'online' : 'offline'}
      </span>
      <span className="call-mac__dot" aria-hidden="true" />
    </p>
  )
}

function BlockedView() {
  const c = useCall()
  const s = c.state
  const lock = useContentLock()
  const blocker = s.blocker ?? callBlockerFrom(s.error ?? 'failed')
  const mac = c.macStatus?.name
  const copy = blockerCopy(blocker, mac)
  const unlocking = blocker.kind === 'locked' && lock.status === 'unlocking'
  const failure = blocker.kind === 'locked' && lock.status === 'locked' ? lock.failure : undefined
  const lockError = blocker.kind === 'locked' && lock.status === 'locked' ? lock.error : undefined
  const offline = !c.macStatus?.online
  const busyLabel =
    lock.status === 'unlocking' && lock.phase === 'machine' && lock.machineName
      ? `Unlocking via ${lock.machineName}…`
      : 'Waiting for your passkey…'
  const detail = [blocker.detail, failure?.detail, lockError].filter(Boolean).join(' · ')
  const Icon = blocker.kind === 'offline' ? Laptop : blocker.kind === 'mic' ? MicOff : Lock
  return (
    <section className="call-blocked" aria-labelledby="call-blocked-h" data-testid="call-blocked">
      <span className="call-blocked__ic">
        <Icon size={24} aria-hidden="true" />
      </span>
      <h2 id="call-blocked-h">{copy.title}</h2>
      <p className="call-blocked__body">{copy.body}</p>
      {failure ? (
        <p className="call-blocked__why" role="status">
          {machineFailureText(failure)}
        </p>
      ) : lockError ? (
        <p className="call-blocked__why" role="status">
          The passkey didn’t finish. Try again when you’re ready.
        </p>
      ) : null}
      <MacStatus />
      <div className="call-blocked__acts">
        <button
          type="button"
          className="call-btn-pri call-btn-lg"
          disabled={unlocking || (blocker.kind !== 'locked' && offline && !!c.macStatus)}
          onClick={() => void c.start()}
          data-testid="call-blocked-action"
        >
          {unlocking ? (
            <LoaderCircle size={18} aria-hidden="true" className="call-spin" />
          ) : blocker.kind === 'locked' ? (
            <KeyRound size={18} aria-hidden="true" />
          ) : (
            <Phone size={18} aria-hidden="true" />
          )}
          {unlocking ? busyLabel : copy.action}
        </button>
        <button type="button" className="call-btn-sec call-btn-lg" onClick={() => c.dismiss()}>
          Not now
        </button>
      </div>
      {detail ? (
        <details className="call-details">
          <summary>Details</summary>
          <code>{detail}</code>
        </details>
      ) : null}
    </section>
  )
}

function stateLine(s: CallState) {
  if (s.phase === 'starting')
    return { label: `Connecting to ${s.machine?.name ?? 'your Mac'}`, cls: 'think', wave: 'still' }
  if (s.muted) return { label: 'Muted', cls: 'muted', wave: 'still' }
  if (s.agent === 'speaking') return { label: 'Speaking', cls: 'speak', wave: 'speak' }
  if (s.agent === 'thinking') return { label: 'Thinking', cls: 'think', wave: 'still' }
  if (s.ptt || s.listening) return { label: 'Listening', cls: 'listen', wave: 'listen' }
  return { label: 'Ready', cls: 'ready', wave: 'still' }
}

function LiveView() {
  const c = useCall()
  const s = c.state
  const [typed, setTyped] = useState('')
  const [queueOpen, setQueueOpen] = useState(() => !isPhoneLayout())
  const logRef = useRef<HTMLDivElement>(null)
  const items = effectiveQueue(s)
  const cur = currentItem(s)
  const prog = queueProgress(s)
  const queued = s.secure.filter((x) => x.state === 'queued')
  const answered = items.filter((x) => x.state === 'answered').length
  const skipped = items.filter((x) => x.state === 'skipped').length
  const upcoming = items.filter((x) => x.state === 'pending')
  const line = stateLine(s)

  // Captions follow the conversation (no animation: scrolling is instant).
  const tail = `${s.captions.length}:${s.captions.at(-1)?.text ?? ''}:${s.interim}`
  useEffect(() => {
    const el = logRef.current
    if (el && tail) el.scrollTop = el.scrollHeight
  }, [tail])

  // ⌘↵ approves the passkey batch (an explicit approve).
  useEffect(() => {
    if (!queued.length) return
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        void c.approveSecure()
      }
    }
    addEventListener('keydown', onKey)
    return () => removeEventListener('keydown', onKey)
  }, [queued.length, c])

  const StateIcon =
    line.cls === 'think'
      ? LoaderCircle
      : line.cls === 'speak'
        ? AudioLines
        : line.cls === 'muted'
          ? MicOff
          : Mic

  return (
    <>
      {prog.total ? (
        <div className="call-prog">
          <div className="call-prog__lab">
            <span className="call-prog__l">
              {prog.done >= prog.total ? (
                <>
                  <CircleCheck size={16} aria-hidden="true" className="call-ok" />
                  Queue clear{' '}
                  <span className="num call-ok">
                    {prog.total} of {prog.total}
                  </span>
                </>
              ) : (
                <>
                  <Diamond size={14} aria-hidden="true" className="call-wait call-dia" />
                  Needs you{' '}
                  <span className="num call-wait" data-testid="call-progress">
                    {prog.position} of {prog.total}
                  </span>
                </>
              )}
            </span>
            {upcoming.length || answered || queued.length ? (
              <button
                type="button"
                className="call-prog__q"
                aria-expanded={queueOpen}
                aria-controls="call-queue"
                onClick={() => setQueueOpen((o) => !o)}
              >
                Queue
                <ChevronRight size={16} aria-hidden="true" className="call-chev" />
              </button>
            ) : null}
          </div>
          <div className="call-segs" aria-hidden="true">
            {items
              .filter((x) => x.kind === 'decision' || x.kind === 'blocker')
              .map((x) => (
                <span key={x.key} className={`call-seg is-${x.state}`} />
              ))}
          </div>
        </div>
      ) : null}

      {s.error ? (
        <div className="call-problem" role="alert">
          <p>{liveProblemText(s.error)}</p>
          <details className="call-details">
            <summary>Details</summary>
            <code>{s.error}</code>
          </details>
        </div>
      ) : null}

      {cur ? (
        <CallItemCard
          item={cur}
          position={`${prog.position} of ${prog.total}`}
          onPick={(n, note) => void c.pick(n, note)}
          onNote={(t) => void c.note(t)}
          onSkip={() => void c.skip()}
        />
      ) : s.phase === 'live' && s.queueSeen ? (
        <section className="call-empty" data-testid="call-empty" aria-labelledby="call-empty-h">
          <CircleCheck size={22} aria-hidden="true" className="call-ok" />
          <div>
            <h2 id="call-empty-h">
              {skipped ? 'That’s the queue — ask anything' : 'Nothing needs you — ask anything'}
            </h2>
            <p className="call-meta">
              {skipped
                ? `${skipped} skipped ${skipped === 1 ? 'stays' : 'stay'} on Home for later. `
                : ''}
              Try “what’s running?” or “show me the latest build”.
            </p>
          </div>
        </section>
      ) : (
        <section className="call-skel" aria-hidden="true">
          <span />
          <span />
          <span />
        </section>
      )}

      <div className="call-caps" ref={logRef} role="log" aria-live="polite" aria-label="Captions">
        {s.captions.map((cap, i) => (
          <p
            key={cap.id}
            className={`call-cap is-${cap.who}${cap.live ? ' is-live' : ''}${i < s.captions.length - 2 ? ' is-old' : ''}`}
          >
            <span className="call-cap__who">{cap.who === 'you' ? 'You' : 'Viko'}</span>
            {cap.cut ? (
              <span>
                {cap.cut.heard}
                <span
                  className="call-cut"
                  title="You cut in here; Viko knows you didn't hear the rest"
                >
                  {' '}
                  ‖ {cap.cut.unheard}
                </span>
              </span>
            ) : (
              <span>{cap.text}</span>
            )}
          </p>
        ))}
        {s.interim ? (
          <p className="call-cap is-you is-live">
            <span className="call-cap__who">You</span>
            <span className="call-partial">{s.interim}</span>
          </p>
        ) : null}
      </div>

      <div className={`call-line is-${line.cls}`} role="status" data-testid="call-state">
        <span className="call-line__l">
          <StateIcon
            size={18}
            aria-hidden="true"
            className={line.cls === 'think' ? 'call-spin' : ''}
          />
          {line.label}
        </span>
        <span className="call-line__w">
          {line.cls === 'think' ? (
            <span className="call-meta">
              {s.phase === 'starting'
                ? 'Starting the call agent'
                : `On ${s.machine?.name ?? 'your Mac'}`}
            </span>
          ) : (
            <CallWave mode={line.wave as 'listen' | 'speak' | 'still'} />
          )}
        </span>
      </div>

      <form
        className="call-type"
        onSubmit={(e) => {
          e.preventDefault()
          const t = typed.trim()
          if (!t) return
          setTyped('')
          void c.say(t)
        }}
      >
        <input
          aria-label="Type to Viko"
          placeholder="Type to Viko"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
      </form>

      {queueOpen && (upcoming.length || answered || queued.length) ? (
        <section id="call-queue" className="call-q" aria-label="Queue">
          {answered ? (
            <div className="call-q__row">
              <CircleCheck size={16} aria-hidden="true" className="call-ok" />
              <span>
                <span className="num">{answered}</span> answered this call
              </span>
              <span />
            </div>
          ) : null}
          {items
            .filter((x) => x.state === 'current' || x.state === 'pending')
            .slice(0, 6)
            .map((x, i) => (
              <div
                key={x.key}
                className={`call-q__row${x.state === 'current' ? ' is-current' : ''}`}
              >
                <Diamond size={14} aria-hidden="true" className="call-wait call-dia" />
                <span>
                  {x.projectName}: {x.title}
                </span>
                <span className="num call-meta">{answered + i + 1}</span>
              </div>
            ))}
          {queued.length ? (
            <button
              type="button"
              className="call-q__row is-link"
              onClick={() => c.show({ type: 'secure-batch' })}
            >
              <KeyRound size={16} aria-hidden="true" className="call-acc" />
              <span>
                <span className="num">{queued.length}</span> wait for your passkey
              </span>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          ) : null}
        </section>
      ) : null}

      {queued.length ? (
        <section className="call-secure" aria-label="Secure batch">
          <p className="call-small">Secure batch</p>
          <p className="call-meta">
            {queued.length} {queued.length === 1 ? 'action' : 'actions'} ·{' '}
            {queued.some((x) => x.production)
              ? 'includes a production ship'
              : 'landings and passkey decisions'}
          </p>
          <button type="button" className="call-btn-pri" onClick={() => void c.approveSecure()}>
            <KeyRound size={16} aria-hidden="true" />
            Approve {queued.length} with passkey
          </button>
          <p className="call-meta call-kbd-hint">
            Or press <kbd>⌘</kbd>
            <kbd>↵</kbd>. Voice can’t approve these.
          </p>
        </section>
      ) : null}

      <div className="call-dock__f">
        <button
          type="button"
          className={`call-ctl${s.muted ? ' is-on' : ''}`}
          aria-label={s.muted ? 'Unmute' : 'Mute'}
          aria-pressed={s.muted}
          onClick={() => c.toggleMute()}
        >
          {s.muted ? <MicOff size={22} aria-hidden="true" /> : <Mic size={22} aria-hidden="true" />}
        </button>
        <button
          type="button"
          className={`call-ptt${s.ptt ? ' is-on' : ''}`}
          aria-keyshortcuts="Meta+Shift+V"
          disabled={s.phase !== 'live'}
          onPointerDown={() => c.pttDown()}
          onPointerUp={() => c.pttUp()}
          onPointerLeave={() => s.ptt && c.pttUp()}
        >
          {/* The mute toggle beside it carries the mic icon; one mic per control row. */}
          {s.ptt ? 'Listening…' : 'Hold to talk'}
          <span className="call-kcs" aria-hidden="true">
            <kbd>⌘</kbd>
            <kbd>⇧</kbd>
            <kbd>V</kbd>
          </span>
        </button>
        <button
          type="button"
          className="call-ctl is-end"
          aria-label="End call"
          onClick={() => void c.end()}
        >
          <PhoneOff size={22} aria-hidden="true" />
        </button>
      </div>
    </>
  )
}

function EndedView() {
  const c = useCall()
  const s = c.state
  const sum = callSummary(s)
  const shown = sum.open.slice(0, 3)
  return (
    <section className="call-ended" aria-labelledby="call-ended-h" data-testid="call-summary">
      <div className="call-ended__h">
        <span className="call-ended__ic">
          <PhoneOff size={20} aria-hidden="true" />
        </span>
        <div>
          <h2 id="call-ended-h">Call ended</h2>
          <p className="call-meta">
            <span className="num" data-testid="call-duration">
              {formatCallTime(sum.durationMs)}
            </span>{' '}
            with {s.machine?.name ?? 'your Mac'}
          </p>
        </div>
      </div>
      {/* Quiet calls close by themselves (CallProvider); everything else waits for Done. */}
      {sum.quiet ? (
        <p className="call-meta" data-testid="call-quiet">
          Nothing changed on this call. Closing…
        </p>
      ) : (
        <ul className="call-sum" aria-label="What this call did">
          {sum.answered ? (
            <li>
              <CircleCheck size={18} aria-hidden="true" className="call-ok" />
              <span>
                <span className="num">{sum.answered}</span>{' '}
                {sum.answered === 1 ? 'decision answered' : 'decisions answered'}
              </span>
            </li>
          ) : null}
          {sum.skipped ? (
            <li>
              <SkipForward size={18} aria-hidden="true" className="call-t3" />
              <span>
                <span className="num">{sum.skipped}</span> skipped
              </span>
            </li>
          ) : null}
          {sum.approved ? (
            <li>
              <KeyRound size={18} aria-hidden="true" className="call-ok" />
              <span>
                <span className="num">{sum.approved}</span> approved with your passkey
              </span>
            </li>
          ) : null}
          {sum.waiting.length ? (
            <li className="is-wait">
              <KeyRound size={18} aria-hidden="true" className="call-wait" />
              <span>
                <span className="num">{sum.waiting.length}</span> still{' '}
                {sum.waiting.length === 1 ? 'waits' : 'wait'} for your passkey
                <button
                  type="button"
                  className="call-btn-pri call-sum__act"
                  onClick={() => void c.approveSecure()}
                >
                  <KeyRound size={16} aria-hidden="true" />
                  Approve {sum.waiting.length} with passkey
                </button>
              </span>
            </li>
          ) : null}
          {sum.open.length ? (
            <li className="is-wait">
              <Diamond size={16} aria-hidden="true" className="call-wait call-dia" />
              <span>
                <span className="num">{sum.open.length}</span> still{' '}
                {sum.open.length === 1 ? 'needs' : 'need'} you
                <span className="call-sum__links">
                  {shown.map((x) => (
                    <Link
                      key={x.key}
                      to={
                        x.decision
                          ? homeHref({ v: 'needs', i: `decision:${x.decision.id}` })
                          : homeHref({ v: 'needs' })
                      }
                      onClick={() => c.dismiss()}
                    >
                      {x.projectName}: {x.title}
                    </Link>
                  ))}
                  {sum.open.length > shown.length ? (
                    <Link to={homeHref({ v: 'needs' })} onClick={() => c.dismiss()}>
                      {sum.open.length - shown.length} more on Home
                    </Link>
                  ) : null}
                </span>
              </span>
            </li>
          ) : (
            <li>
              <CircleSlash size={18} aria-hidden="true" className="call-t3" />
              <span>Nothing else needs you right now</span>
            </li>
          )}
        </ul>
      )}
      <div className="call-ended__acts">
        <button
          type="button"
          className="call-btn-pri call-btn-lg"
          onClick={() => void c.start()}
          disabled={!c.mac}
        >
          <Phone size={18} aria-hidden="true" />
          Call again
        </button>
        <button type="button" className="call-btn-sec call-btn-lg" onClick={() => c.dismiss()}>
          Done
        </button>
      </div>
    </section>
  )
}
